<template>
  <div>
    <SearchFilterHeader 
      ref="searchFilterRef" 
      :raw-filter-config="rawFilterConfig" 
      :ignored-keys="ignoredKeys" 
      :disable-shadow="disableShadow"
      :disable-column-dropdown="disableColumnDropdown"
      @search-change="searchQuery = $event" 
      @filters-change="filterChange"
      @columns-change="columnVisibility = $event" 
    />

    <DataViewer
      :search-on-column="searchOnColumn"
      :rows="rawData"
      :filters="filters"
      :search-query="searchQuery"
      :column-visibility="columnVisibility"
      :actions="actions"
      :scrollable="scrollable"
      :disable-shadow="disableShadow"
      :editable-columns="editableColumns"
      :editable-column-types="editableColumnTypes"
      :disable-column-dropdown="disableColumnDropdown"
      :ignored-keys="ignoredKeys"
      :error="error"
      :error-message="errorMessage"
      :empty-message="emptyMessage"
      :loading="loading"
      :row-editable-state="rowEditableState"
      :toggle-columns="toggleColumns"
      :badge-columns="badgeColumns"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { createFilterChange } from '@/Modules/Automationer/filterFetcher';

import SearchFilterHeader from '@/components/Automation/Filtering/SearchFilterHeader.vue';
import DataViewer from '@/components/Automation/Table/DataViewer.vue';

const searchFilterRef = ref(null)
const rawData = ref([])
const filters = ref([])
const columnVisibility = ref([])
const searchQuery = ref('')
const error = ref(false)
const errorMessage = ref('No data available or failed to load.')
const emptyMessage = ref('')
const loading = ref(false)

const props = defineProps({
  dataHandler: {
    type: Function,
    required: false
  },
  rawFilterConfig: {
    type: Object,
    default: () => ({})
  },
  ignoredKeys: {
    type: Set,
    default: () => new Set()
  },
  searchOnColumn: {
    type: String,
    required: true
  },
  advancedPollingSortBy: {
    type: String,
    required: false,
    default: 'Period'
  },
  actions: {
    type: [Array, Function],
    required: true,
    default: () => []
  },
  disableColumnDropdown: {
    type: Boolean,
    default: false
  },
  dataMapper: {
    type: Function,
    required: false
  },
  scrollable: {
    type: Boolean,
    default: false
  },
  disableShadow: {
    type: Boolean,
    default: false
  },
  editableColumns: {
    type: Array,
    default: () => []
  },
  editableColumnTypes: {
    type: Object,
    default: () => ({})
  },
  rowEditableState: {
    type: Object,
    default: () => ({})
  },
  toggleColumns: {
    type: Object,
    default: () => ({})
  },
  badgeColumns: {
    type: Object,
    default: () => ({})
  }
})

const populateComponent = (data, options = {}) => {
  loading.value = false

  if (data === null || (Array.isArray(data) && data.length === 0)) {
    rawData.value = []
    if (options.emptyMessage) {
      emptyMessage.value = options.emptyMessage
      error.value = false
    } else {
      error.value = true
      errorMessage.value = 'No data available or failed to load.'
    }
    return
  }

  if (props.dataMapper) {
    data = props.dataMapper(data)
  }

  rawData.value = data.map(row => reactive({ ...row }))
  searchFilterRef.value?.Initialize(data)
  error.value = false
  emptyMessage.value = ''
}

const setReady = (value) => {
  if (!value) {
    rawData.value = []
    loading.value = true
    error.value = false
    emptyMessage.value = ''
  } else {
  }
}

const setLoading = (value) => { 
  loading.value = !value
}

const filterChange = async ($event) => {
  try {
    loading.value = true
    await createFilterChange(
      props.dataHandler,
      filters,
      rawData,
      props.advancedPollingSortBy,
      loading,
      error,
      errorMessage
    )($event)
  } catch (err) {
    error.value = true
    errorMessage.value = err?.message || 'Filtering failed.'
    emptyMessage.value = ''
    rawData.value = []
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  if (props.dataHandler) {
    try {
      loading.value = true
      const data = await props.dataHandler()
      populateComponent(data)
    } catch (err) {
      error.value = true
      errorMessage.value = err?.message || 'Data loading failed.'
      emptyMessage.value = ''
      rawData.value = []
    } finally {
      loading.value = false
    }
  }
})

defineExpose({
  populateComponent,
  setReady,
  setLoading
})
</script>
